import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';

function LabourCostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [labourCost, setLabourCost] = useState({
    product_name: '',
    product_code: '',
    type: 'Service',
    product_category: 'Labour',
    product_description: '',
    price: '',
    sales_tax_rate: '20% VAT',
    location: '',
    cost_price: 0,
    markup: 0
  });

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!isNew) {
      fetchLabourCost();
    }
  }, [id]);

  const fetchLabourCost = async () => {
    try {
      const response = await apiFetch(`/api/products/${id}`);
      const data = await response.json();
      setLabourCost(data);
    } catch (error) {
      console.error('Error fetching labour cost:', error);
    }
  };

  const saveLabourCost = async () => {
    if (!labourCost.product_name || !labourCost.product_code || !labourCost.price) {
      alert('Name, Code, and Price are required');
      return;
    }

    setSaving(true);
    try {
      const labourCostData = {
        ...labourCost,
        type: 'Service',
        product_category: 'Labour',
        price: parseFloat(labourCost.price || 0),
        cost_price: parseFloat(labourCost.cost_price || 0),
        markup: parseFloat(labourCost.markup || 0)
      };

      const url = isNew 
        ? '/api/products'
        : `/api/products/${id}`;
      
      const method = isNew ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(labourCostData)
      });

      if (response.ok) {
        alert(isNew ? 'Labour cost created!' : 'Labour cost updated!');
        navigate('/labour-costs');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving labour cost:', error);
      alert('Failed to save labour cost');
    } finally {
      setSaving(false);
    }
  };

  const deleteLabourCost = async () => {
    if (!window.confirm('Are you sure you want to delete this labour cost?')) {
      return;
    }

    setDeleting(true);
    try {
      const response = await apiFetch(`/api/products/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Labour cost deleted!');
        navigate('/labour-costs');
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting labour cost:', error);
      alert('Failed to delete labour cost');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/labour-costs')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">
                {isNew ? 'Add Labour Cost' : labourCost.product_name}
              </h1>
              {labourCost.product_code && (
                <p className="text-sm text-blue-100 mt-1">Code: {labourCost.product_code}</p>
              )}
            </div>
            <div className="flex gap-2">
              {!isNew && (
                <button
                  onClick={deleteLabourCost}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
                >
                  {deleting ? '⏳ Deleting...' : '🗑️ Delete'}
                </button>
              )}
              <button
                onClick={saveLabourCost}
                disabled={saving}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
              >
                {saving ? '⏳ Saving...' : '💾 Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-8">

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Product Name */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Labour Cost Name *
            </label>
            <input
              type="text"
              value={labourCost.product_name}
              onChange={(e) => setLabourCost({ ...labourCost, product_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Zone 1 - Standard 3 Day Response"
            />
          </div>

          {/* Product Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Code *
            </label>
            <input
              type="text"
              value={labourCost.product_code}
              onChange={(e) => setLabourCost({ ...labourCost, product_code: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., LABOUR-Z1-STD"
            />
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price (£) *
            </label>
            <input
              type="number"
              step="0.01"
              value={labourCost.price}
              onChange={(e) => setLabourCost({ ...labourCost, price: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="220.00"
            />
          </div>

          {/* Location/Postcodes */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Postcodes Covered
            </label>
            <input
              type="text"
              value={labourCost.location}
              onChange={(e) => setLabourCost({ ...labourCost, location: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., CM, SS, CO, IP, NR"
            />
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={labourCost.product_description}
              onChange={(e) => setLabourCost({ ...labourCost, product_description: e.target.value })}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Detailed description of this labour cost..."
            />
          </div>

          {/* Sales Tax Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              VAT Rate
            </label>
            <select
              value={labourCost.sales_tax_rate}
              onChange={(e) => setLabourCost({ ...labourCost, sales_tax_rate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="20% VAT">20% VAT</option>
              <option value="0% VAT">0% VAT (Zero-rated)</option>
              <option value="Exempt">VAT Exempt</option>
            </select>
          </div>

          {/* Cost Price (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cost Price (£) <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="number"
              step="0.01"
              value={labourCost.cost_price}
              onChange={(e) => setLabourCost({ ...labourCost, cost_price: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00"
            />
          </div>

        </div>
      </div>
    </div>
    </div>
  );
}

export default LabourCostDetail;
